// Settings › Categories. A category's dialog has a Details tab and, once it exists, the rules its assets carry —
// Certificates required (CR_20260921_Asset_Lifecycle T1, §7.2). Managers only, as every settings screen.
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '../../auth/session';
import { Empty, Loading, LoadError, Page } from '../../components/Page';
import { CertRulesTab } from '../../components/CertRules';
import { ServiceRulesTab } from '../../components/ServiceRules';
import { useServiceRules } from '../../data/service';
import { useCertRules } from '../../data/certificates';
import { keys, useCategories } from '../../data/queries';
import { supabase } from '../../lib/supabase';

type Tier = 'qr' | 'ble' | 'gps';
interface Category {
  id: string;
  name: string;
  default_tier: Tier;
}

function CategoryDialog({ category, onClose }: { category?: Category; onClose(): void }) {
  const { t } = useTranslation();
  const { me } = useSession();
  const queryClient = useQueryClient();
  const [name, setName] = useState(category?.name ?? '');
  const [tier, setTier] = useState<Tier>(category?.default_tier ?? 'ble');
  const [tab, setTab] = useState<'details' | 'certs' | 'service'>('details');
  const save = useMutation({
    mutationFn: async () => {
      const row = { name: name.trim(), default_tier: tier };
      const { error } = category
        ? await supabase().from('asset_categories').update(row).eq('id', category.id)
        : await supabase()
            .from('asset_categories')
            .insert({ ...row, company_id: me!.company!.id });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.categories });
      onClose();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth={tab === 'details' ? 'xs' : 'md'}>
      <DialogTitle>
        {category ? `${t('common.edit')} · ${category.name}` : t('settings.categories.new')}
      </DialogTitle>
      <Box sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tab} onChange={(_, v: 'details' | 'certs' | 'service') => setTab(v)}>
          <Tab value="details" label={t('certs.details_tab')} />
          <Tab value="certs" label={t('certs.rules_tab')} data-testid="category-tab-certs" />
          <Tab value="service" label={t('svc.tab')} data-testid="category-tab-service" />
        </Tabs>
      </Box>
      {tab !== 'details' ? (
        <>
          <DialogContent>
            {category ? (
              tab === 'certs' ? (
                <CertRulesTab categoryId={category.id} />
              ) : (
                <ServiceRulesTab categoryId={category.id} />
              )
            ) : (
              <Alert severity="info">{t('certs.rules_save_first')}</Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>{t('common.close')}</Button>
          </DialogActions>
        </>
      ) : (
        <form onSubmit={submit}>
          <DialogContent>
            <Stack spacing={2}>
              <TextField
                label={t('settings.categories.name')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoFocus
              />
              <TextField
                select
                label={t('settings.categories.default_tier')}
                value={tier}
                onChange={(e) => setTier(e.target.value as Tier)}
              >
                {(['qr', 'ble', 'gps'] as const).map((x) => (
                  <MenuItem key={x} value={x}>
                    {t(`settings.categories.tier_${x}`)}
                  </MenuItem>
                ))}
              </TextField>
              {save.error && <Alert severity="error">{(save.error as Error).message}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>{t('common.cancel')}</Button>
            <Button type="submit" variant="contained" disabled={save.isPending || !name.trim()}>
              {t('common.save')}
            </Button>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
}

export function CategoriesPage() {
  const { t } = useTranslation();
  const categories = useCategories();
  const rules = useCertRules();
  const serviceRules = useServiceRules();
  const ruleCount = (id: string) => (rules.data ?? []).filter((r) => r.category_id === id).length;
  const serviceCount = (id: string) =>
    (serviceRules.data ?? []).filter((r) => r.category_id === id).length;
  const [editing, setEditing] = useState<Category | 'new' | null>(null);
  return (
    <Page
      title={t('settings.categories.title')}
      actions={
        <Button variant="contained" onClick={() => setEditing('new')}>
          {t('settings.categories.new')}
        </Button>
      }
    >
      {categories.isLoading ? (
        <Loading />
      ) : categories.error ? (
        <LoadError error={categories.error} />
      ) : !categories.data?.length ? (
        <Empty>—</Empty>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('settings.categories.name')}</TableCell>
              <TableCell>{t('settings.categories.default_tier')}</TableCell>
              <TableCell>{t('certs.rules_col')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {categories.data.map((c) => (
              <TableRow key={c.id} hover>
                <TableCell>{c.name}</TableCell>
                <TableCell>{t(`settings.categories.tier_${c.default_tier}`)}</TableCell>
                <TableCell sx={{ color: 'text.secondary' }}>
                  {[
                    ruleCount(c.id) ? t('certs.rules_count', { count: ruleCount(c.id) }) : null,
                    serviceCount(c.id)
                      ? `${serviceCount(c.id)} × ${t('svc.title').toLowerCase()}`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ') || '—'}
                </TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => setEditing(c)}>
                    {t('common.edit')}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {editing && (
        <CategoryDialog
          category={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </Page>
  );
}
